/**
 * Image Compression Utility
 * 
 * Handles client-side image compression to reduce file sizes
 * before storing as base64 in the database.
 */

// Default configuration - can be overridden
const DEFAULT_CONFIG = {
  maxSizeKB: 200, // Increased from 50KB to 200KB for better quality
  compressionQuality: 0.85, // Increased from 0.5 to 0.85 for much better quality
  maxWidth: 2560, // Increased from 1920 to 2560 for higher resolution
  maxHeight: 1440, // Increased from 1080 to 1440 for higher resolution
  outputFormat: 'image/webp' // Use WebP for better compression efficiency
};

/**
 * Convert file size from bytes to KB
 */
export const bytesToKB = (bytes) => {
  return bytes / 1024;
};

/**
 * Convert base64 string to file size in KB
 */
export const getBase64SizeKB = (base64String) => {
  // Remove data URL prefix if present
  const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');
  
  // Calculate actual byte size (base64 is ~33% larger than actual data)
  const actualBytes = (base64Data.length * 3) / 4;
  
  // Subtract padding characters
  const padding = base64Data.match(/=/g) ? base64Data.match(/=/g).length : 0;
  return (actualBytes - padding) / 1024;
};

/**
 * Load image from file or base64 string
 */
const loadImage = (source) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    
    if (source instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(source);
    } else if (typeof source === 'string') {
      img.src = source;
    } else {
      reject(new Error('Invalid image source'));
    }
  });
};

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  let { width, height } = { width: originalWidth, height: originalHeight };
  
  // Scale down if needed
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }
  
  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }
  
  return { width: Math.round(width), height: Math.round(height) };
};

/**
 * Check if browser supports WebP format
 */
const supportsWebP = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

/**
 * Get optimal output format based on browser support
 */
const getOptimalFormat = (preferredFormat = 'image/webp') => {
  if (preferredFormat === 'image/webp' && supportsWebP()) {
    return 'image/webp';
  }
  return 'image/jpeg'; // Fallback to JPEG
};

/**
 * Compress image to canvas with specified quality
 */
const compressImageToCanvas = (img, targetWidth, targetHeight, quality, format) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  
  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Draw image with new dimensions
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  
  // Convert to base64 with optimal format
  const optimalFormat = getOptimalFormat(format);
  return canvas.toDataURL(optimalFormat, quality);
};

/**
 * Apply image sharpening to improve perceived quality
 */
const applySharpening = (canvas, ctx) => {
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // Simple unsharp mask implementation
  const kernel = [
    0, -1, 0,
    -1, 5, -1,
    0, -1, 0
  ];
  
  const width = canvas.width;
  const height = canvas.height;
  const output = new Uint8ClampedArray(data);
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) { // RGB channels only
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4 + c;
            const kernelIdx = (ky + 1) * 3 + (kx + 1);
            sum += data[idx] * kernel[kernelIdx];
          }
        }
        const idx = (y * width + x) * 4 + c;
        output[idx] = Math.max(0, Math.min(255, sum));
      }
    }
  }
  
  imageData.data.set(output);
  ctx.putImageData(imageData, 0, 0);
};

/**
 * Calculate adaptive quality based on image characteristics
 */
const calculateAdaptiveQuality = (img, targetSizeKB) => {
  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const totalPixels = img.naturalWidth * img.naturalHeight;
  
  // Base quality on image complexity and size
  let baseQuality = 0.85;
  
  // Adjust for aspect ratio (very wide/tall images may need different treatment)
  if (aspectRatio > 3 || aspectRatio < 0.33) {
    baseQuality += 0.05; // Slightly higher quality for extreme aspect ratios
  }
  
  // Adjust for image size
  if (totalPixels > 2000000) { // Large images
    baseQuality += 0.05;
  } else if (totalPixels < 500000) { // Small images
    baseQuality -= 0.05;
  }
  
  // Adjust for target size
  if (targetSizeKB > 150) {
    baseQuality += 0.05;
  } else if (targetSizeKB < 100) {
    baseQuality -= 0.05;
  }
  
  return Math.max(0.3, Math.min(0.95, baseQuality));
};

/**
 * Compress image with progressive quality reduction and adaptive quality
 */
const compressWithProgressiveQuality = async (img, targetWidth, targetHeight, targetSizeKB, format) => {
  // Start with adaptive quality instead of fixed 0.9
  let quality = calculateAdaptiveQuality(img, targetSizeKB);
  let compressed;
  let attempts = 0;
  const maxAttempts = 12; // Increased attempts for better quality
  
  do {
    compressed = compressImageToCanvas(img, targetWidth, targetHeight, quality, format);
    const currentSizeKB = getBase64SizeKB(compressed);
    
    console.log(`Compression attempt ${attempts + 1}: ${currentSizeKB.toFixed(1)}KB at quality ${quality.toFixed(2)}`);
    
    if (currentSizeKB <= targetSizeKB) {
      break;
    }
    
    // More gradual quality reduction for better results
    quality -= 0.08; // Reduced from 0.1 to 0.08 for more gradual reduction
    attempts++;
    
    if (quality < 0.2) {
      quality = 0.2; // Increased minimum quality from 0.1 to 0.2
    }
    
  } while (attempts < maxAttempts && quality >= 0.2);
  
  return compressed;
};

/**
 * Main compression function with enhanced quality processing
 */
export const compressImage = async (source, config = {}) => {
  const settings = { ...DEFAULT_CONFIG, ...config };
  
  try {
    // Load the image
    const img = await loadImage(source);
    
    // Calculate optimal dimensions
    const { width, height } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      settings.maxWidth,
      settings.maxHeight
    );
    
    console.log(`Original: ${img.naturalWidth}x${img.naturalHeight}`);
    console.log(`Target: ${width}x${height}`);
    
    // Use adaptive quality for initial compression
    const adaptiveQuality = calculateAdaptiveQuality(img, settings.maxSizeKB);
    console.log(`Adaptive quality: ${adaptiveQuality.toFixed(2)}`);
    
    // First, try with target dimensions and adaptive quality
    let compressed = compressImageToCanvas(
      img, 
      width, 
      height, 
      adaptiveQuality, 
      settings.outputFormat
    );
    
    let currentSizeKB = getBase64SizeKB(compressed);
    console.log(`Initial compression: ${currentSizeKB.toFixed(1)}KB`);
    
    // If still too large, use progressive quality reduction
    if (currentSizeKB > settings.maxSizeKB) {
      console.log(`Size exceeds ${settings.maxSizeKB}KB, applying progressive compression...`);
      compressed = await compressWithProgressiveQuality(
        img, 
        width, 
        height, 
        settings.maxSizeKB, 
        settings.outputFormat
      );
      currentSizeKB = getBase64SizeKB(compressed);
    }
    
    // Apply sharpening for better perceived quality (optional enhancement)
    if (settings.enableSharpening !== false) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const tempImg = new Image();
        
        await new Promise((resolve, reject) => {
          tempImg.onload = () => {
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(tempImg, 0, 0, width, height);
            applySharpening(canvas, ctx);
            compressed = canvas.toDataURL(getOptimalFormat(settings.outputFormat), adaptiveQuality);
            resolve();
          };
          tempImg.onerror = reject;
          tempImg.src = compressed;
        });
        
        currentSizeKB = getBase64SizeKB(compressed);
        console.log(`After sharpening: ${currentSizeKB.toFixed(1)}KB`);
      } catch (sharpeningError) {
        console.warn('Sharpening failed, using original compressed image:', sharpeningError);
      }
    }
    
    console.log(`Final size: ${currentSizeKB.toFixed(1)}KB`);
    
    return {
      success: true,
      dataUrl: compressed,
      originalSize: source instanceof File ? bytesToKB(source.size) : getBase64SizeKB(source),
      compressedSize: currentSizeKB,
      compressionRatio: currentSizeKB / (source instanceof File ? bytesToKB(source.size) : getBase64SizeKB(source)),
      dimensions: { width, height },
      qualityUsed: adaptiveQuality,
      formatUsed: getOptimalFormat(settings.outputFormat)
    };
    
  } catch (error) {
    console.error('Image compression failed:', error);
    return {
      success: false,
      error: error.message,
      dataUrl: null
    };
  }
};

/**
 * Check if image needs compression
 */
export const needsCompression = (source, maxSizeKB = DEFAULT_CONFIG.maxSizeKB) => {
  if (source instanceof File) {
    return bytesToKB(source.size) > maxSizeKB;
  } else if (typeof source === 'string') {
    return getBase64SizeKB(source) > maxSizeKB;
  }
  return false;
};

/**
 * Get app configuration for image compression
 */
export const getImageConfig = async () => {
  try {
    // In a real app, this would fetch from your backend API
    // For now, return default config
    return DEFAULT_CONFIG;
  } catch (error) {
    console.warn('Failed to fetch image config, using defaults:', error);
    return DEFAULT_CONFIG;
  }
};

/**
 * Process image for rich text editor
 * This is the main function to be called from the editor
 */
export const processImageForEditor = async (source, customConfig = {}) => {
  const config = { ...DEFAULT_CONFIG, ...customConfig };
  
  // Check if compression is needed
  if (!needsCompression(source, config.maxSizeKB)) {
    console.log('Image is within size limits, no compression needed');
    
    // Still return the image as base64 if it's a file
    if (source instanceof File) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            success: true,
            dataUrl: e.target.result,
            originalSize: bytesToKB(source.size),
            compressedSize: bytesToKB(source.size),
            compressionRatio: 1,
            wasCompressed: false
          });
        };
        reader.readAsDataURL(source);
      });
    } else {
      return {
        success: true,
        dataUrl: source,
        originalSize: getBase64SizeKB(source),
        compressedSize: getBase64SizeKB(source),
        compressionRatio: 1,
        wasCompressed: false
      };
    }
  }
  
  // Compress the image
  console.log('Image exceeds size limits, compressing...');
  const result = await compressImage(source, config);
  
  if (result.success) {
    result.wasCompressed = true;
  }
  
  return result;
}; 